'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check, FileText, Hash, Type, Clock, FileInput, AlignLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { paragraphCounterContent } from '../../data/paragraph-counter-content';
import Link from 'next/link';

type CountResult = {
    paragraphs: number;
    words: number;
    characters: number;
    charactersNoSpaces: number;
    readingTime: number;
    avgWordsPerParagraph: number;
};

export default function ParagraphCounter() {
    const [text, setText] = useState('');
    const [countResult, setCountResult] = useState<CountResult>({
        paragraphs: 0,
        words: 0,
        characters: 0,
        charactersNoSpaces: 0,
        readingTime: 0,
        avgWordsPerParagraph: 0
    });
    const [copied, setCopied] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [wordsPerMinute, setWordsPerMinute] = useState(200);
    const { toast } = useToast();

    const calculateCounts = useCallback((text: string) => {
        if (!text.trim()) {
            return {
                paragraphs: 0,
                words: 0,
                characters: 0,
                charactersNoSpaces: 0,
                readingTime: 0,
                avgWordsPerParagraph: 0
            };
        }

        // Basic counts
        const characters = text.length;
        const charactersNoSpaces = text.replace(/\s+/g, '').length;

        // Split into paragraphs (split by double newlines or more)
        const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0);
        const paragraphCount = paragraphs.length;

        // Count words
        const words = text.trim() === '' ? [] : text.trim().split(/\s+/);
        const wordCount = words.length;

        // Calculate reading time (in minutes)
        const readingTime = Math.ceil(wordCount / wordsPerMinute) || 1;

        // Calculate average words per paragraph
        const avgWordsPerParagraph = paragraphCount > 0
            ? Number((wordCount / paragraphCount).toFixed(1))
            : 0;

        return {
            paragraphs: paragraphCount,
            words: wordCount,
            characters,
            charactersNoSpaces,
            readingTime,
            avgWordsPerParagraph
        };
    }, [wordsPerMinute]);

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = e.target.value;
        setText(newText);
        setCountResult(calculateCounts(newText));
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const fileContent = event.target?.result as string;
            setText(fileContent);
            setCountResult(calculateCounts(fileContent));
        };
        reader.readAsText(file);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    };

    const clearText = () => {
        setText('');
        setCountResult({
            paragraphs: 0,
            words: 0,
            characters: 0,
            charactersNoSpaces: 0,
            readingTime: 0,
            avgWordsPerParagraph: 0
        });
    };

    const readingTimeLabel = countResult.readingTime === 1 ? 'minute' : 'minutes';

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Paragraph Counter">
                Count paragraphs in text with our free online tool. Analyze paragraph structure, count words per paragraph, and improve text organization.
            </ToolPageIntro>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <div>
                                    <CardTitle>Text Input</CardTitle>
                                    <CardDescription>Type, paste, or upload text to analyze paragraphs</CardDescription>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        className="text-xs"
                                    >
                                        {showAdvanced ? 'Hide Details' : 'Show Details'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="relative">
                                <Textarea
                                    value={text}
                                    onChange={handleTextChange}
                                    placeholder="Type or paste your text here..."
                                    className="min-h-[300px] w-full text-base"
                                    rows={10}
                                />
                                <div className="absolute right-3 top-3 flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={copyToClipboard}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Copy to clipboard"
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearText}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Clear text"
                                    >
                                        <RefreshCw className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="relative">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        asChild
                                        className="cursor-pointer"
                                    >
                                        <Label htmlFor="file-upload" className="cursor-pointer">
                                            <FileInput className="h-4 w-4 mr-2" />
                                            Upload File
                                            <input
                                                id="file-upload"
                                                type="file"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                accept=".txt,.md,.doc,.docx,.pdf,.odt,.rtf"
                                            />
                                        </Label>
                                    </Button>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className="text-sm text-muted-foreground">Reading speed:</span>
                                    <select
                                        value={wordsPerMinute}
                                        onChange={(e) => setWordsPerMinute(Number(e.target.value))}
                                        className="bg-background border rounded px-2 py-1 text-sm"
                                    >
                                        <option value="150">Slow (150 wpm)</option>
                                        <option value="200">Average (200 wpm)</option>
                                        <option value="250">Fast (250 wpm)</option>
                                    </select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Text Analysis</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="text-center">
                                <div className="text-5xl font-bold">{countResult.paragraphs.toLocaleString()}</div>
                                <div className="text-muted-foreground">Paragraphs</div>
                            </div>

                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <StatCard
                                        icon={<Type className="h-4 w-4" />}
                                        label="Words"
                                        value={countResult.words.toLocaleString()}
                                    />
                                    <StatCard
                                        icon={<Hash className="h-4 w-4" />}
                                        label="Characters"
                                        value={countResult.characters.toLocaleString()}
                                    />
                                </div>

                                <div className="border-t pt-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center text-sm text-muted-foreground">
                                            <Clock className="h-4 w-4 mr-1" />
                                            <span>Reading time:</span>
                                        </div>
                                        <span className="font-medium">~{countResult.readingTime} {readingTimeLabel}</span>
                                    </div>
                                </div>

                                {showAdvanced && (
                                    <div className="border-t pt-4 mt-4">
                                        <h3 className="font-medium mb-3">Advanced Metrics</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">Characters (no spaces):</span>
                                                <span className="font-medium">{countResult.charactersNoSpaces.toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">Avg. words per paragraph:</span>
                                                <span className="font-medium">{countResult.avgWordsPerParagraph}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">Reading speed:</span>
                                                <span className="font-medium">{wordsPerMinute} wpm</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>About Paragraph Counter</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm text-muted-foreground">
                            <p>
                                This tool helps you analyze text by counting paragraphs, words, and characters, along with providing reading time estimates and other metrics.
                            </p>
                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">How it works:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Paragraphs are split by blank lines (double line breaks)</li>
                                    <li>Words are sequences of characters separated by whitespace</li>
                                    <li>Character count includes all visible and whitespace characters</li>
                                    <li>Reading time is estimated based on the selected reading speed</li>
                                </ul>
                            </div>
                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">Common uses:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Academic writing and essay formatting</li>
                                    <li>Content creation and blog writing</li>
                                    <li>Meeting paragraph count requirements</li>
                                    <li>Analyzing document structure</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={paragraphCounterContent.title}
                description={paragraphCounterContent.description}
                content={paragraphCounterContent.content}
                faqs={paragraphCounterContent.faqs}
                relatedTools={paragraphCounterContent.relatedTools}
            />
        </div>
    );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
    return (
        <div className="border rounded-lg p-3">
            <div className="flex items-center space-x-2 text-muted-foreground">
                {icon}
                <span className="text-sm">{label}</span>
            </div>
            <p className="mt-1 text-xl font-semibold">{value}</p>
        </div>
    );
}
